import About from './About'
import Action, { STATE_HIDE } from './Action'
import Chapter, { STATE_CHAPTER_SHOW } from './Chapter'
import DebugUI from './DebugUI'
import Footer from './Footer'
import Intro, { STATE_TITLE_INTRO, STATE_TITLE_OUTRO, STATE_DESCRIPTION_OUTRO } from './Intro'
import Loader from './Loader'
import Overlay from './Overlay'
import Menu from './Menu'
import Sound from './Sound'
import { createDOM, getRotateCSS, addClass, removeClass, setBodyRatioFontSize } from './helpers/DOMHelper'
import { ease, Easing } from './helpers/EaseHelper'
import { Mouse } from './helpers/MouseHelper'
import Timer from './helpers/TimerHelper'
import * as bowser from 'bowser'

class Interface {
  constructor(onResize=()=>{}, onFrame=()=>{}, onIntroComplete=()=>{}) {
    this._loaderPercent = 0
    this._textDebug = false
    this.fxProps = {
      scale: 1,
      rotateX: 0,
      rotateY: 0,
      rotateStrength: 0
    }
    this.root = document.querySelector('#root')
    this.canvas = document.querySelector('#render3d')
    this.layerFX = document.querySelector('#fx')
    this.debugger = new DebugUI()
    this.menu = null
    this.loader = new Loader(document.querySelector('#loader'))
    this.overlay = new Overlay(document.querySelector('#overlay'))
    this.intro = new Intro(document.querySelector('#intro'), this.onIntroStateChange, () => this.onIntroComplete())
    this.about = new About(document.querySelector('#about'))
    this.footer = new Footer(document.querySelector('#footer'))
    this.action = new Action(document.querySelector('#action'), this.onActionStateChange)
    this.chapter = new Chapter(document.querySelector('#chapter'), this.onChapterStateChange)
    this.sound = new Sound('./BM-1_v2.mp3','./BM-2_v2.mp3', this.footer.buttonSound)
    this.onResize = onResize
    this.onFrame = onFrame
    this.onIntroComplete = onIntroComplete
    window.addEventListener('resize', () => this.resize())
    Timer.onFrame = (time, delta) => {
      this.update(time, delta)
      this.onFrame(time, delta)
    }
    this.showLoader()
    this.action.on('update', () => { 
      this.sound.mix = this.action.timeline.ratio
      this.actionRatio = this.action.timeline.ratio
    })
    this.actionRatio = 0
  }

  onIntroStateChange = state => {
    switch (state) {
      case STATE_TITLE_INTRO:
        ease(this.fxProps).delay(2000).to({scale: 1.4, rotateStrength: 1}, 5000).easing(Easing.Cubic.InOut).start()
      break
      case STATE_TITLE_OUTRO:
        ease(this.fxProps).to({scale: 1.6}, 5000).easing(Easing.Cubic.InOut).start()
      break
      case STATE_DESCRIPTION_OUTRO:
        ease(this.fxProps).to({scale: 1.2, rotateStrength: 0}, 3000).easing(Easing.Cubic.InOut).start()
        this.sound.play()
        removeClass(this.root, 'intro')
      break
      default:
      break
    }
  }

  onChapterStateChange = state => {
    switch (state) {
      case STATE_CHAPTER_SHOW:
        this.actionRatio = 0
        break
      default:
    }
  }

  onActionStateChange = state => {
    switch (state) {
      case STATE_HIDE:
        if (this.sound) this.sound.swap(0, 5000)
        break
      default:
    }
  }

  /**
   * loader
   */
  get loaderPercent () { return this._loaderPercent }
  set loaderPercent (value) {
    this.loader.progress = value
    this._loaderPercent = value
  }

  showLoader () {
    addClass(this.root, 'loading')
    // this.loader.show() executed by indes.js on preloader loaded
  }

  hideLoader () {
    this.loader.hide().then(() => {
      removeClass(this.root, 'loading')
      addClass(this.root, 'intro')
    })
  }

  /**
   * menu
   */
  buildMenu (buttons, onSelect, onToggle) {
    this.menu = new Menu(document.querySelector('#menu'), buttons, document.querySelector('#menu-button'))
    this.menu.onSelect = id => { onSelect(id) }
    this.menu.onToggleMenu = value => {
      onToggle(value)
      this.sound.swap(value ? 1 : 0, 500)
      if (value) ease(this).to({ actionRatio: 0 }, 1500).start()
    }
    this.menu.aboutButton.view.addEventListener('click', () => this.about.show())
  }

  updateMenu (id) { this.menu.selected = id }

  /**
   * actions
   */
  displayAction () {
    this.action.setAction(...arguments)
    this.action.show()
  }

  hideAction () { this.action.hide(...arguments) }

  /**
   * text debug
   */
  get textDebug () { return this._textDebug }
  set textDebug (value) {
    this._textDebug = value
    this.displayTextDebug(value)
  }

  displayTextDebug (enable) {
    const styles = [
      'text__chapter--hand',
      'text__chapter',
      'text__action-chapter',
      'text__action',
      'text__action--hand',
      'text__menu-button',
      'text__menu-name',
      'text__menu-index',
      'text__loading'
    ]

    if (enable && !document.querySelector('.text__display')) {
      const container = createDOM('div', 'text__display')
      this.root.appendChild(container)
      styles.map(style => {
        const textContainer = createDOM('div', 'text__container ' + style, 'Black messiah 1234567890')
        const nameContainer = createDOM('div', 'text__name', style)
        container.appendChild(nameContainer)
        container.appendChild(textContainer)
        return textContainer
      })

    } else if (!enable && document.querySelector('.text__display')) {
      this.root.removeChild(document.querySelector('.text__display'))
    }
  }

  update (time, delta) {
    this.intro.update(time, delta)
    this.action.update(time, delta)
    this.about.update(time, delta)
    if (this._loaderPercent < 100 || this.about.isOpen) {
      this.layerFX.style.transform = `scale3d(${this.fxProps.scale}, ${this.fxProps.scale}, 1)`
    } else {
      if (!bowser.safari) {
        this.fxProps.rotateX += (Mouse.ry - this.fxProps.rotateX) * 0.1 * this.fxProps.rotateStrength
        this.fxProps.rotateY += (Mouse.rx - this.fxProps.rotateY) * 0.1 * this.fxProps.rotateStrength
      }
      this.layerFX.style.transform = getRotateCSS(this.fxProps.rotateX * -1, this.fxProps.rotateY, 0, 10) + ` scale3d(${this.fxProps.scale}, ${this.fxProps.scale}, 1)`
    }
  }
  
  resize () {
    setBodyRatioFontSize()
    this.onResize()
  }

  stop () { Timer.stop() }
  play () { Timer.play() }
}
export default Interface;


// WEBPACK FOOTER //
// ./src/scripts/Interface.js